interface Ga4UserIdPayload {
  event?: 'user_identified' | 'user_logged_out';
  user_id: string | null;
}

interface DataLayerWindow extends Window {
  dataLayer?: Ga4UserIdPayload[];
}

/**
 * Survives `IdentifyUsers` remounts (`template.tsx` remounts on client
 * navigations) so `user_identified` is not pushed again for the same session.
 */
let lastPushedUserId: string | null = null;

function getDataLayerWindow(): DataLayerWindow | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window as DataLayerWindow;
}

function pushToDataLayer(payload: Ga4UserIdPayload): boolean {
  const dataLayerWindow = getDataLayerWindow();
  if (!dataLayerWindow) {
    return false;
  }

  dataLayerWindow.dataLayer = dataLayerWindow.dataLayer ?? [];
  dataLayerWindow.dataLayer.push(payload);
  return true;
}

/**
 * Exposes Clerk user id to the GTM/GA4 dataLayer so warehouse joins can key
 * GA4 events to the same user grain. Does not push email or other PII.
 *
 * Idempotent for the same user id — signup identify plus later `IdentifyUsers`
 * mounts (including every platform route change) must not re-fire
 * `user_identified` for GTM.
 *
 * GTM: map dataLayer `user_id` onto the GA4 Configuration tag User-ID field,
 * and optionally fire on the `user_identified` event.
 */
export function pushGa4UserId(userId: string): void {
  if (!userId || lastPushedUserId === userId) {
    return;
  }

  if (pushToDataLayer({ event: 'user_identified', user_id: userId })) {
    lastPushedUserId = userId;
  }
}

export function clearGa4UserId(): void {
  if (lastPushedUserId === null) {
    return;
  }

  if (pushToDataLayer({ event: 'user_logged_out', user_id: null })) {
    lastPushedUserId = null;
  }
}
